import React, { useState } from 'react';
import {
  Typography,
  Card,
  Row,
  Col,
  Button,
  Table,
  Tag,
  Select,
  Space,
  Statistic,
  Modal,
  Descriptions,
  Alert,
  Tooltip,
  Badge,
} from 'antd';
import {
  ReloadOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useWebhookLogs, useWebhookStats, useRetryWebhook, useWebhookHealth } from '../hooks/useTatumWebhook';
import { WebhookLog } from '../api/tatumWebhook';
import dayjs from 'dayjs';
import ReactJson from 'react-json-pretty';
import 'react-json-pretty/themes/monikai.css';

const { Title, Text } = Typography;
const { Option } = Select;

const TatumWebhookLogsPage: React.FC = () => {
  const { t } = useTranslation();

  // State for filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);

  // State for modal
  const [selectedLog, setSelectedLog] = useState<WebhookLog | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  // Calculate skip value for pagination
  const skip = (currentPage - 1) * pageSize;

  // Fetch data
  const { data: logsData, isLoading, error, refetch } = useWebhookLogs(statusFilter, pageSize, skip);
  const { data: statsData, isLoading: isStatsLoading } = useWebhookStats();
  const { data: healthData } = useWebhookHealth();
  const { mutate: retryWebhook, isPending: isRetrying } = useRetryWebhook();

  // Handle view details
  const handleViewDetails = (log: WebhookLog) => {
    setSelectedLog(log);
    setIsModalVisible(true);
  };

  // Handle retry webhook
  const handleRetry = (requestId: string) => {
    Modal.confirm({
      title: 'Retry Webhook',
      content: `Are you sure you want to retry webhook ${requestId}?`,
      onOk: () => {
        retryWebhook(requestId);
      },
    });
  };

  // Get status tag
  const getStatusTag = (status: string) => {
    switch (status) {
      case 'success':
        return <Tag icon={<CheckCircleOutlined />} color="success">Success</Tag>;
      case 'failed':
        return <Tag icon={<CloseCircleOutlined />} color="error">Failed</Tag>;
      case 'resolved':
        return <Tag icon={<CheckCircleOutlined />} color="processing">Resolved</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Request ID',
      dataIndex: 'request_id',
      key: 'request_id',
      width: 250,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text copyable={{ text }}>{text.substring(0, 30)}...</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Blockchain',
      key: 'blockchain',
      width: 120,
      render: (_: any, record: WebhookLog) => {
        const chain = record.payload?.chain || 'N/A';
        return <Tag color="blue">{chain}</Tag>;
      },
    },
    {
      title: 'Type',
      key: 'type',
      width: 100,
      render: (_: any, record: WebhookLog) => {
        const type = record.payload?.type || 'N/A';
        return <Tag>{type}</Tag>;
      },
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 120,
      render: (_: any, record: WebhookLog) => {
        const amount = record.payload?.amount || 'N/A';
        return <Text>{amount}</Text>;
      },
    },
    {
      title: 'Processing Time',
      dataIndex: 'processing_time_ms',
      key: 'processing_time_ms',
      width: 130,
      render: (time: number) => time ? `${time}ms` : 'N/A',
    },
    {
      title: 'Retry Count',
      dataIndex: 'retry_count',
      key: 'retry_count',
      width: 100,
      render: (count: number) => count || 0,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: WebhookLog) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            View
          </Button>
          {record.status === 'failed' && (
            <Button
              type="link"
              size="small"
              icon={<SyncOutlined />}
              onClick={() => handleRetry(record.request_id)}
              loading={isRetrying}
            >
              Retry
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={2}>
        <ApiOutlined /> Tatum Webhook Logs
      </Title>

      {/* Health Status Alert */}
      {healthData && (
        <Alert
          message={
            <Space>
              <Text strong>Server Status:</Text>
              <Badge status={healthData.status === 'healthy' ? 'success' : 'error'} text={healthData.status} />
              <Text type="secondary">| MongoDB: {healthData.config?.mongoConnected ? '✓' : '✗'}</Text>
              <Text type="secondary">| Redis: {healthData.config?.redisConnected ? '✓' : '✗'}</Text>
              <Text type="secondary">| HMAC: {healthData.config?.hmacConfigured ? '✓' : '✗'}</Text>
            </Space>
          }
          type={healthData.status === 'healthy' ? 'success' : 'warning'}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {statsData?.stats.byStatus.map((stat) => (
          <Col xs={24} sm={12} md={6} key={stat._id}>
            <Card>
              <Statistic
                title={stat._id.charAt(0).toUpperCase() + stat._id.slice(1)}
                value={stat.count}
                suffix={
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    ({stat.avgProcessingTime.toFixed(0)}ms avg)
                  </Text>
                }
                valueStyle={{
                  color: stat._id === 'success' ? '#3f8600' : stat._id === 'failed' ? '#cf1322' : '#1890ff',
                }}
              />
            </Card>
          </Col>
        ))}
        {statsData?.stats.last24Hours && (
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Last 24h Success Rate"
                value={statsData.stats.last24Hours.successRate}
                valueStyle={{ color: '#3f8600' }}
                suffix={
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    ({statsData.stats.last24Hours.total} total)
                  </Text>
                }
              />
            </Card>
          </Col>
        )}
      </Row>

      {/* Filters and Actions */}
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            style={{ width: 200 }}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Filter by status"
          >
            <Option value="all">All Status</Option>
            <Option value="success">Success</Option>
            <Option value="failed">Failed</Option>
            <Option value="resolved">Resolved</Option>
          </Select>

          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={isLoading}
          >
            Refresh
          </Button>
        </Space>
      </Card>

      {/* Logs Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={logsData?.logs || []}
          rowKey="_id"
          loading={isLoading}
          scroll={{ x: 1400 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: logsData?.total || 0,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} webhooks`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size || 20);
            },
          }}
        />
      </Card>

      {/* Details Modal */}
      <Modal
        title={`Webhook Details: ${selectedLog?.request_id}`}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            Close
          </Button>,
          selectedLog?.status === 'failed' && (
            <Button
              key="retry"
              type="primary"
              icon={<SyncOutlined />}
              onClick={() => {
                if (selectedLog) {
                  handleRetry(selectedLog.request_id);
                  setIsModalVisible(false);
                }
              }}
              loading={isRetrying}
            >
              Retry
            </Button>
          ),
        ]}
      >
        {selectedLog && (
          <div>
            <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Status" span={2}>
                {getStatusTag(selectedLog.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Request ID" span={2}>
                <Text copyable>{selectedLog.request_id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Processing Time">
                {selectedLog.processing_time_ms ? `${selectedLog.processing_time_ms}ms` : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Retry Count">
                {selectedLog.retry_count || 0}
              </Descriptions.Item>
              <Descriptions.Item label="HTTP Status">
                {selectedLog.http_status_code || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Error Type">
                {selectedLog.error_type || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Created At" span={2}>
                {dayjs(selectedLog.created_at).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              {selectedLog.resolved_at && (
                <Descriptions.Item label="Resolved At" span={2}>
                  {dayjs(selectedLog.resolved_at).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
              )}
            </Descriptions>

            {selectedLog.error_message && (
              <Alert
                message="Error Message"
                description={selectedLog.error_message}
                type="error"
                style={{ marginBottom: 16 }}
              />
            )}

            <Card title="Webhook Payload" size="small" style={{ marginBottom: 16 }}>
              <ReactJson data={selectedLog.payload} />
            </Card>

            {selectedLog.response && (
              <Card title="Response" size="small">
                <ReactJson data={selectedLog.response} />
              </Card>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TatumWebhookLogsPage;

